/**
 * CDN Utility Functions for FCDN Integration
 * 
 * Provides optimized image URLs with:
 * - Auto-optimization (?auto=1)
 * - Network hints (?hint=wifi/4g/3g/2g)
 * - Quality/width/format parameters
 * 
 * @see docs/FRONTEND_API_DOCUMENTATION.md
 */

// CDN Base URL
export const CDN_BASE_URL = "https://fcdn.manabakh.ir/images";

/**
 * Network hint types supported by FCDN
 * @type {Object}
 */
export const NETWORK_HINTS = {
    WIFI: "wifi",
    FIVEG: "5g",
    FOURG: "4g",
    THREEG: "3g",
    TWOG: "2g",
};

/**
 * Default CDN parameters for TV apps
 * TV is always on stable WiFi network
 */
const TV_DEFAULT_PARAMS = {
    auto: false,
    quality: 95,
    format: 'jpeg'
};

/**
 * Detect network type using Navigator API
 * Falls back to 'wifi' for TV (stable connection assumed)
 * 
 * @returns {string} Network hint (wifi, 4g, 3g, 2g)
 */
export const getNetworkHint = () => {
    // Check if Network Information API is available
    // Note: Not supported on legacy TVs, will fallback to wifi
    if (typeof navigator !== "undefined" && navigator.connection) {
        const connection = navigator.connection;
        const effectiveType = connection.effectiveType;

        // Map effectiveType to CDN hints
        switch (effectiveType) {
            case "slow-2g":
            case "2g":
                return NETWORK_HINTS.TWOG;
            case "3g":
                return NETWORK_HINTS.THREEG;
            case "4g":
                return NETWORK_HINTS.FOURG;
            default:
                return NETWORK_HINTS.WIFI;
        }
    }

    // Default to WiFi for TV apps (stable connection)
    return NETWORK_HINTS.WIFI;
};

/**
 * Build CDN query parameters string
 * 
 * @param {Object} options - CDN parameters
 * @param {boolean} [options.auto=true] - Enable auto-optimization
 * @param {string} [options.hint] - Network hint override
 * @param {number} [options.width] - Image width (w=)
 * @param {number} [options.quality] - Image quality 1-100 (q=)
 * @param {string} [options.format] - Image format: webp, avif, jpeg (f=)
 * @returns {string} Query string (e.g., "?auto=1&hint=wifi")
 */
export const buildCDNParams = (options = {}) => {
    const params = [];
    const {
        auto = true,
        hint,
        width,
        quality,
        format,
    } = options;

    // Auto-optimization (network-aware quality/format)
    if (auto) {
        params.push("auto=1");
    }

    // Network hint (override auto-detection)
    const networkHint = hint || getNetworkHint();
    if (networkHint) {
        params.push(`hint=${networkHint}`);
    }

    // Manual parameters (override auto if specified)
    if (width) {
        params.push(`w=${width}`);
    }
    if (quality) {
        params.push(`q=${quality}`);
    }
    if (format) {
        params.push(`f=${format}`);
    }

    return params.length > 0 ? `?${params.join("&")}` : "";
};

/**
 * Add CDN optimization parameters to image path
 * Handles paths that already have query parameters
 * 
 * @param {string} imagePath - Image path or filename
 * @param {Object} [options] - CDN parameters (see buildCDNParams)
 * @returns {string} Path with CDN params appended
 */
export const addCDNParams = (imagePath, options = {}) => {
    if (!imagePath) return imagePath;

    // If already a full URL with params, just return
    if (imagePath.includes("?auto=1") || imagePath.includes("?auto=true")) {
        return imagePath;
    }

    const cdnParams = buildCDNParams(options);

    // Handle existing query params
    if (imagePath.includes("?")) {
        return `${imagePath}&${cdnParams.substring(1)}`; // Remove leading ?
    }

    return `${imagePath}${cdnParams}`;
};

/**
 * Get full CDN URL with optimization parameters
 * 
 * @param {string} imagePath - Image path (relative or absolute)
 * @param {Object} [options] - CDN parameters
 * @param {boolean} [options.raw=false] - Return URL without CDN optimization params
 * @returns {string|null} Full CDN URL or null if no path
 */
export const getOptimizedCDNUrl = (imagePath, options = TV_DEFAULT_PARAMS) => {
    if (!imagePath) return null;
    const { raw = false } = options || {};

    // Already a full URL
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
        if (raw) return imagePath;

        // If it's our CDN, add params; otherwise return as-is
        if (imagePath.includes("fcdn.manabakh.ir")) {
            return addCDNParams(imagePath, options);
        }
        return imagePath;
    }

    // Build full CDN URL
    const fullUrl = `${CDN_BASE_URL}/${imagePath}`;
    if (raw) return fullUrl;

    return addCDNParams(fullUrl, options);
};

/**
 * Get optimized image set (LQIP, WebP, original) with CDN params
 * 
 * @param {Object} photoItem - Photo object from CMS
 * @param {string} [photoItem.src] - Original source
 * @param {string} [photoItem.webp] - WebP source
 * @param {string} [photoItem.lqip] - LQIP source
 * @param {Object} [options] - CDN parameters
 * @returns {Object} { src, webpSrc, lqipSrc }
 */
export const getOptimizedImageSet = (photoItem, options = TV_DEFAULT_PARAMS) => {
    if (!photoItem) {
        return { src: null, webpSrc: null, lqipSrc: null };
    }

    return {
        src: getOptimizedCDNUrl(photoItem.src, options),
        webpSrc: getOptimizedCDNUrl(photoItem.webp, options),
        // LQIP typically doesn't need auto-optimization (already tiny)
        lqipSrc: photoItem.lqip ? `${CDN_BASE_URL}/${photoItem.lqip}` : null,
    };
};

/**
 * Preload critical image with CDN optimization
 * Adds <link rel="preload"> to document head
 * 
 * @param {string} imagePath - Image to preload
 * @param {Object} [options] - CDN parameters
 */
export const preloadImage = (imagePath, options = TV_DEFAULT_PARAMS) => {
    if (!imagePath || typeof document === "undefined") return;

    const url = getOptimizedCDNUrl(imagePath, options);
    if (!url) return;

    // Check if already preloaded
    const existing = document.querySelector(`link[href="${url}"]`);
    if (existing) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    link.type = "image/webp"; // CDN returns WebP by default with auto=1
    document.head.appendChild(link);
};

const cdnUtils = {
    CDN_BASE_URL,
    NETWORK_HINTS,
    getNetworkHint,
    buildCDNParams,
    addCDNParams,
    getOptimizedCDNUrl,
    getOptimizedImageSet,
    preloadImage,
};

export default cdnUtils;
